/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { CardMetadata, ProductCard, Typography2, breakpoints } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

const styles = {
  cardContainer: css`
    && {
      ${breakpoints.down('xs')} {
        max-width: 100%;
      }
    }
  `,
};

type Props = {
  id: string;
  slug: string;
  title: string;
  description?: string;
  publishedDate?: string;
  estimatedReadingTime?: string;
  // This is an exposed prop for additional functionality.
  onClick?: () => void;
  metadata: string[];
  eventingData?: Record<string, any>;
};

const ArticleCard = ({ slug, title, metadata, description, onClick, eventingData }: Props) => {
  const track = useTracker();

  const articleEventingData = {
    article: {
      articleSlug: slug ?? '',
      articleTitle: title ?? undefined,
    },
    ...eventingData,
  };

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_article_card',
    { ...articleEventingData },
    undefined,
    { fullyVisible: true }
  );

  const handleClick = () => {
    track('click_article_card', { ...articleEventingData });
    onClick?.();
  };

  return (
    <ProductCard
      css={styles.cardContainer}
      productType="article"
      ref={visibilityTrackingRef}
      variant="grid"
      onClick={handleClick}
      title={{
        name: title,
        linkProps: {
          href: `/articles/${slug}`,
        },
      }}
      body={
        <Typography2 variant="bodySecondary" component="p" color="supportText">
          {description}
        </Typography2>
      }
      footer={<CardMetadata data-e2e="article-metadata" metadata={metadata} />}
    />
  );
};

export { ArticleCard };

export default ArticleCard;
